import { useCallback, useRef, useState } from 'react';
import { SocialProfile } from '../auth/SocialProfileModal';
import { ProfileData } from '../auth/CompleteProfileModal';
import {
  createUser,
  getUserByAuth0Id,
  updateUserProfile,
  uploadFile,
} from '../../services/api';
import { extractSocialProfileFromAuth0, filterAllowedProfiles, detectSocialProfile } from '../auth/authHelpers';

interface UseProgressiveProfileOptions {
  auth0Logout: (opts: { logoutParams: { returnTo: string } }) => void;
  setUser: (user: any) => void;
  setLoading: (loading: boolean) => void;
  setErrorModalOpen: (open: boolean) => void;
  isRequestingToSpot: boolean;
  clearAuthCredential: () => void;
}

export function useProgressiveProfile({
  auth0Logout,
  setUser,
  setLoading,
  setErrorModalOpen,
  isRequestingToSpot,
  clearAuthCredential,
}: UseProgressiveProfileOptions) {
  const [emailCollectionOpen, setEmailCollectionOpen] = useState(false);
  const [pendingAuth0User, setPendingAuth0User] = useState<any>(null);
  const [socialProfileOpen, setSocialProfileOpen] = useState(false);
  const [completeProfileOpen, setCompleteProfileOpen] = useState(false);
  const [profileAccountCreated, setProfileAccountCreated] = useState(false);
  const [collectedEmail, setCollectedEmail] = useState<string | null>(null);
  const createdProfileUserRef = useRef<any>(null);
  const uploadedProfileImageUrlRef = useRef<string | null>(null);
  const uploadedProfileImageFileRef = useRef<File | null>(null);

  const persistCompletedUser = useCallback((backendUser: any) => {
    setUser(backendUser);
    localStorage.setItem('currentUser', JSON.stringify(backendUser));
    localStorage.setItem('userId', String(backendUser.id));
    localStorage.setItem('username', backendUser.username);
    sessionStorage.setItem('needs_onboarding', 'true');
    sessionStorage.removeItem('isRequestingToSpot');
  }, [setUser]);

  const finalizeUserCreation = useCallback(async (opts: { auth0User: any; email: string; extraProfiles?: SocialProfile[] }) => {
    const { auth0User, email, extraProfiles = [] } = opts;

    const baseUsername = email.split('@')[0];
    const uniqueUsername = `${baseUsername}_${Date.now()}`;
    const extracted = extractSocialProfileFromAuth0(auth0User);
    const allowed = filterAllowedProfiles(extracted);
    const social_profiles = [...allowed, ...extraProfiles];

    const newUserData = {
      auth0_id: auth0User.sub,
      email,
      username: uniqueUsername,
      display_name: auth0User.name || auth0User.nickname || baseUsername,
      profile_image: auth0User.picture,
      role: 'collaborator',
      creator_type: 'content_creator',
      bio: '',
      social_profiles,
    };

    const backendUser = await createUser(newUserData);
    if (!backendUser || !backendUser.id) {
      console.error('❌ Invalid response from createUser API');
      throw new Error('Failed to create user - invalid response');
    }

    persistCompletedUser(backendUser);
    setPendingAuth0User(null);
    setCollectedEmail(null);
    return backendUser;
  }, [persistCompletedUser]);

  const handleEmailProvided = useCallback(async (providedEmail: string) => {
    if (!pendingAuth0User) return;

    setEmailCollectionOpen(false);
    setLoading(true);

    try {
      const extracted = extractSocialProfileFromAuth0(pendingAuth0User);
      const allowed = filterAllowedProfiles(extracted);

      if (!allowed || allowed.length === 0) {
        setCollectedEmail(providedEmail);
        setCompleteProfileOpen(true);
        return;
      }

      await finalizeUserCreation({ auth0User: pendingAuth0User, email: providedEmail, extraProfiles: [] });
    } catch (error: any) {
      console.error('❌ Failed to create user with provided email:', error);
      setErrorModalOpen(true);
      auth0Logout({ logoutParams: { returnTo: window.location.origin } });
    } finally {
      setLoading(false);
    }
  }, [pendingAuth0User, setLoading, setErrorModalOpen, auth0Logout, finalizeUserCreation]);

  const handleEmailCollectionCancel = useCallback(() => {
    setEmailCollectionOpen(false);
    setPendingAuth0User(null);

    const pendingSpot = sessionStorage.getItem('pendingGuestSpotRequest');
    if (pendingSpot) {
      const returnUrl = `/creator/${pendingSpot}/request`;
      sessionStorage.setItem('returnAfterLogout', returnUrl);
    }

    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  }, [auth0Logout]);

  const handleSocialProfileProvided = useCallback(async (profile: SocialProfile) => {
    if (!pendingAuth0User) return;
    try {
      setLoading(true);
      await finalizeUserCreation({
        auth0User: pendingAuth0User,
        email: collectedEmail || pendingAuth0User.email,
        extraProfiles: [profile],
      });
      setSocialProfileOpen(false);
    } catch (err) {
      console.error('❌ Failed to create user after social profile provided:', err);
      setErrorModalOpen(true);
      auth0Logout({ logoutParams: { returnTo: window.location.origin } });
    } finally {
      setLoading(false);
    }
  }, [pendingAuth0User, collectedEmail, setLoading, setErrorModalOpen, auth0Logout, finalizeUserCreation]);

  const handleSocialProfileCancel = useCallback(() => {
    setSocialProfileOpen(false);
    setPendingAuth0User(null);
    setCollectedEmail(null);

    const pendingSpot = sessionStorage.getItem('pendingGuestSpotRequest');
    if (pendingSpot) {
      const returnUrl = `/creator/${pendingSpot}/request`;
      sessionStorage.setItem('returnAfterLogout', returnUrl);
    }

    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  }, [auth0Logout]);

  const handleCompleteProfileSubmit = useCallback(async (profileData: ProfileData) => {
    const tempAuth0Id = sessionStorage.getItem('temp_auth0_id');
    const isEmailOTPFlow = !pendingAuth0User && tempAuth0Id && collectedEmail;
    const auth0Id = isEmailOTPFlow ? tempAuth0Id : pendingAuth0User?.sub;

    if (!auth0Id) {
      throw new Error('Your sign-in session expired. Return to the opportunity and sign in again.');
    }

    setLoading(true);
    try {
      const parsedProfile = profileData.socialProfile ? detectSocialProfile(profileData.socialProfile) : null;
      const providerProfiles = pendingAuth0User
        ? filterAllowedProfiles(extractSocialProfileFromAuth0(pendingAuth0User))
        : [];
      const socialProfiles = [
        ...providerProfiles,
        ...(parsedProfile ? [parsedProfile] : []),
      ].filter((profile, index, profiles) => (
        profiles.findIndex((candidate) => (
          candidate.platform === profile.platform
          && candidate.profile_url === profile.profile_url
        )) === index
      ));
      let backendUser = createdProfileUserRef.current;

      if (!backendUser) {
        const newUserData = {
          auth0_id: auth0Id,
          email: profileData.email || collectedEmail || pendingAuth0User?.email,
          email_verification_code: profileData.emailVerificationCode,
          username: profileData.username,
          display_name: profileData.displayName,
          // Keep the provider image until the optional local file can be
          // uploaded under the newly created user's protected namespace.
          profile_image: profileData.profileImage || pendingAuth0User?.picture,
          role: 'collaborator',
          bio: profileData.bio || '',
          social_profiles: socialProfiles,
        };

        try {
          backendUser = await createUser(newUserData);
        } catch (createError) {
          // A lost response or an old double-submit can leave the account
          // successfully created even though the client saw an error. Recover
          // only the account owned by this same validated Auth0 subject.
          try {
            backendUser = await getUserByAuth0Id(auth0Id);
          } catch {
            throw createError;
          }
        }

        if (!backendUser || !backendUser.id) {
          throw new Error('The account service returned an incomplete profile. Please try again.');
        }
        createdProfileUserRef.current = backendUser;
        setProfileAccountCreated(true);
        // Account identity now exists even if the optional media step fails or
        // the tab reloads. Preserve onboarding before attempting Cloudinary.
        sessionStorage.setItem('needs_onboarding', 'true');
      }

      if (profileData.profileImageFile) {
        try {
          if (uploadedProfileImageFileRef.current !== profileData.profileImageFile) {
            uploadedProfileImageFileRef.current = profileData.profileImageFile;
            uploadedProfileImageUrlRef.current = null;
          }
          let uploadedUrl = uploadedProfileImageUrlRef.current;
          if (!uploadedUrl) {
            const uploadedImage = await uploadFile(profileData.profileImageFile, 'profiles');
            uploadedUrl = uploadedImage?.url;
            if (!uploadedUrl) {
              throw new Error('The image service did not return a photo URL.');
            }
            uploadedProfileImageUrlRef.current = uploadedUrl;
          }

          const updatedUser = await updateUserProfile(backendUser.id, {
            profile_image: uploadedUrl,
          });
          backendUser = { ...backendUser, ...updatedUser, profile_image: uploadedUrl };
          createdProfileUserRef.current = backendUser;
        } catch (uploadError) {
          console.error('❌ Account created but profile photo could not be saved:', uploadError);
          throw new Error(
            'Your account is ready, but the photo could not be uploaded. Try again, choose another photo, or remove it to continue.'
          );
        }
      }

      persistCompletedUser(backendUser);

      if (isEmailOTPFlow) sessionStorage.removeItem('temp_auth0_id');
      createdProfileUserRef.current = null;
      uploadedProfileImageUrlRef.current = null;
      uploadedProfileImageFileRef.current = null;
      setProfileAccountCreated(false);
      setPendingAuth0User(null);
      setCollectedEmail(null);
      setCompleteProfileOpen(false);
      if (isEmailOTPFlow) {
        window.location.replace('/onboarding');
      }
    } catch (err) {
      // Profile errors are recoverable and belong inline in the still-open
      // dialog. Never log out a user (or discard a pending application) here.
      console.error('❌ Failed to complete profile:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [
    pendingAuth0User,
    collectedEmail,
    setLoading,
    persistCompletedUser,
  ]);

  const handleCompleteProfileCancel = useCallback(() => {
    const tempAuth0Id = sessionStorage.getItem('temp_auth0_id');
    const isEmailOTPFlow = !pendingAuth0User && tempAuth0Id;

    // If account creation succeeded and only the optional image step failed,
    // Cancel means "continue without this photo", not "destroy my session".
    if (createdProfileUserRef.current) {
      persistCompletedUser(createdProfileUserRef.current);
      createdProfileUserRef.current = null;
      uploadedProfileImageUrlRef.current = null;
      uploadedProfileImageFileRef.current = null;
      setProfileAccountCreated(false);
      setCompleteProfileOpen(false);
      setPendingAuth0User(null);
      setCollectedEmail(null);
      if (isEmailOTPFlow) {
        sessionStorage.removeItem('temp_auth0_id');
        window.location.replace('/onboarding');
      }
      return;
    }

    setCompleteProfileOpen(false);
    setProfileAccountCreated(false);
    setPendingAuth0User(null);
    setCollectedEmail(null);

    if (isEmailOTPFlow) {
      sessionStorage.removeItem('temp_auth0_id');
      clearAuthCredential();
    }

    const pendingSpot = sessionStorage.getItem('pendingGuestSpotRequest');
    if (pendingSpot) {
      const returnUrl = `/creator/${pendingSpot}/request`;
      sessionStorage.setItem('returnAfterLogout', returnUrl);
    }

    if (!isEmailOTPFlow) {
      auth0Logout({ logoutParams: { returnTo: window.location.origin } });
    }
  }, [
    pendingAuth0User,
    auth0Logout,
    persistCompletedUser,
    clearAuthCredential,
  ]);

  return {
    // State
    emailCollectionOpen,
    pendingAuth0User,
    socialProfileOpen,
    completeProfileOpen,
    profileAccountCreated,
    collectedEmail,
    // Setters (needed by syncUser effect)
    setPendingAuth0User,
    setCollectedEmail,
    setCompleteProfileOpen,
    setEmailCollectionOpen,
    setSocialProfileOpen,
    // Handlers
    finalizeUserCreation,
    handleEmailProvided,
    handleEmailCollectionCancel,
    handleSocialProfileProvided,
    handleSocialProfileCancel,
    handleCompleteProfileSubmit,
    handleCompleteProfileCancel,
  };
}
